/**
 * Shopify Subscription Price Updater v3 (Event Delegation)
 *
 * This script dynamically updates product prices based on selected purchase options.
 * It uses a robust event delegation strategy to ensure it works reliably in
 * quick view modals and on product pages, without interfering with other scripts
 * or affecting prices on the collection grid.
 */
;(() => {
  // --- Configuration ---
  const PRICE_ELEMENT_SELECTOR = ".product-price--original"
  const PURCHASE_OPTION_SELECTOR = 'input[name^="purchaseOption_"]'
  const QUANTITY_INPUT_SELECTOR = 'input[name="quantity"]'
  const PRODUCT_PAGE_CONTAINER = "product-page"
  const QUICK_VIEW_MODAL_CONTAINER = "modal-box"
  // --- End Configuration ---

  /**
   * The main initialization function. Sets up everything for a given product container.
   * @param {HTMLElement} container The element containing a single product's details.
   */
  function initializeUpdaterFor(container) {
    if (container.dataset.priceUpdaterInitialized === "true") {
      return
    }

    const priceElements = container.querySelectorAll(PRICE_ELEMENT_SELECTOR)
    const purchaseOptions = container.querySelectorAll(PURCHASE_OPTION_SELECTOR)

    if (priceElements.length === 0 || purchaseOptions.length === 0) {
      return
    }

    // Mark as initialized to prevent this function from running twice on the same element.
    container.dataset.priceUpdaterInitialized = "true"

    // Store the original price details for each price display element.
    const priceElementInfo = Array.from(priceElements).map((el) => {
      const originalText = el.innerText.trim()
      const hasFromPrefix = originalText.toLowerCase().startsWith("from ")
      const priceText = hasFromPrefix ? originalText.substring(5) : originalText
      const originalPrice = Number.parseFloat(priceText.replace(/[^0-9.-]+/g, ""))
      return { element: el, hasFromPrefix, originalPrice }
    })

    // The core function that calculates and updates the displayed price.
    const updatePriceDisplay = () => {
      const selectedOption = container.querySelector(`${PURCHASE_OPTION_SELECTOR}:checked`)
      const quantityInput = container.querySelector(QUANTITY_INPUT_SELECTOR)
      const quantity = quantityInput ? Number.parseInt(quantityInput.value, 10) : 1

      priceElementInfo.forEach((info) => {
        let priceToDisplay = info.originalPrice

        if (selectedOption) {
          const priceString = selectedOption.getAttribute("data-variant-price")
          if (priceString) {
            priceToDisplay = Number.parseFloat(priceString.replace(/[^0-9.-]+/g, ""))
          }
        }

        const totalPrice = priceToDisplay * quantity
        const formatter = new Intl.NumberFormat("en-GB", { style: "currency", currency: "GBP" })
        const formattedPrice = formatter.format(totalPrice)

        info.element.innerText = info.hasFromPrefix ? `From ${formattedPrice}` : formattedPrice
      })
    }

    // --- Event Delegation ---
    // Listen for 'change' events on the entire container.
    container.addEventListener("change", (event) => {
      // Check if the event was triggered by a purchase option or quantity input.
      if (event.target.matches(PURCHASE_OPTION_SELECTOR) || event.target.matches(QUANTITY_INPUT_SELECTOR)) {
        updatePriceDisplay()
      }
    })

    updatePriceDisplay() // Run once immediately to set the correct initial price.
  }

  // --- Triggering Logic ---

  // 1. For Quick View Modals: Use a MutationObserver to watch for when they are added to the page.
  const observer = new MutationObserver((mutations) => {
    for (const mutation of mutations) {
      for (const node of mutation.addedNodes) {
        if (node.nodeType === Node.ELEMENT_NODE) {
          const modal = node.matches(QUICK_VIEW_MODAL_CONTAINER) ? node : node.querySelector(QUICK_VIEW_MODAL_CONTAINER)
          if (modal) {
            // Wait a moment for the modal's content (and the subscription app) to fully render.
            setTimeout(() => initializeUpdaterFor(modal), 500)
          }
        }
      }
    }
  })

  observer.observe(document.body, { childList: true, subtree: true })

  // 2. For Standalone Product Pages: Check on page load.
  const initializeOnPageLoad = () => {
    const productPage = document.querySelector(PRODUCT_PAGE_CONTAINER)
    if (productPage) {
      // Poll for a few seconds because the subscription app might load its elements late.
      let attempts = 0
      const interval = setInterval(() => {
        const purchaseOptions = productPage.querySelector(PURCHASE_OPTION_SELECTOR)
        if (purchaseOptions || attempts >= 10) {
          clearInterval(interval)
          if (purchaseOptions) {
            initializeUpdaterFor(productPage)
          }
        }
        attempts++
      }, 500)
    }
  }

  // Run the page load check when the document is ready.
  if (document.readyState === "loading") {
    document.addEventListener("DOMContentLoaded", initializeOnPageLoad)
  } else {
    initializeOnPageLoad()
  }
})()
